import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DirectoryRecordDialog } from '../admin/directory/DirectoryKit';
import { fetchLocationOptions, fetchVendorOptions } from '@/data/procurement';
import {
  createPurchaseOrder,
  purchaseOrderErrorMessage,
  purchaseOrderRecordKeys,
  type NewPurchaseOrderInput,
  type PurchaseOrderKind,
} from '@/data/purchaseOrderRecord';
import usePurchaseOrderRequestKey from './usePurchaseOrderRequestKey';
import './PurchaseOrderRecord.css';

const LINE_USES = [
  { value: 'inventory', label: 'Inventory' },
  { value: 'event_sale', label: 'Event sale' },
] as const;

type PurchaseCreateLineDraft = {
  id: string;
  description: string;
  qty: string;
  unitCost: string;
  disposition: 'inventory' | 'event_sale';
};

type CreateDraft = {
  locationId: string;
  vendorId: string;
  kind: PurchaseOrderKind;
  expectedDay: string;
  notes: string;
};

function createBlankPurchaseLine(): PurchaseCreateLineDraft {
  return {
    id: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `line-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    description: '',
    qty: '1',
    unitCost: '',
    disposition: 'inventory',
  };
}

/** Noon on the chosen day in the viewer's zone, so the site-local day never shifts. */
function expectedDayToTimestamp(day: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const date = new Date(`${day}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function normalizeLines(lines: PurchaseCreateLineDraft[]): NewPurchaseOrderInput['lines'] {
  return lines
    .map((line) => {
      const unitCostRaw = line.unitCost.trim();
      const unitCost = unitCostRaw ? Number(unitCostRaw) : null;
      return {
        description: line.description.trim(),
        qty: Math.max(1, Math.round(Number(line.qty) || 1)),
        unitCost: unitCost != null && Number.isFinite(unitCost) ? unitCost : null,
        disposition: line.disposition,
      };
    })
    .filter((line) => line.description.length > 0);
}

type NewPurchaseOrderDialogPanelProps = {
  onClose: () => void;
  /** Called with the new order; without it the dialog opens the new record itself. */
  onCreated?: (created: { poId: string; docNo: string | null }) => void;
  defaultLocationId?: string | null;
};

export type NewPurchaseOrderDialogProps = NewPurchaseOrderDialogPanelProps & {
  open: boolean;
};

/**
 * "New purchase order" (moved from AdminPurchaseOrdersPage's quick create): site, vendor, type,
 * expected date, notes and lines. Creation goes through create_purchase_order, which checks the
 * feature and the site and assigns the P-number; a retry of the same form reuses its request key,
 * so a lost answer never creates the order twice.
 */
export default function NewPurchaseOrderDialog({ open, onClose, onCreated, defaultLocationId }: NewPurchaseOrderDialogProps) {
  if (!open) return null;
  return <NewPurchaseOrderDialogPanel onClose={onClose} onCreated={onCreated} defaultLocationId={defaultLocationId} />;
}

function NewPurchaseOrderDialogPanel({ onClose, onCreated, defaultLocationId }: NewPurchaseOrderDialogPanelProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const requestKeys = usePurchaseOrderRequestKey();
  const locationRef = React.useRef<HTMLSelectElement | null>(null);
  const [draft, setDraft] = React.useState<CreateDraft>(() => ({
    locationId: defaultLocationId ?? '',
    vendorId: '',
    kind: 'goods',
    expectedDay: '',
    notes: '',
  }));
  const [lines, setLines] = React.useState<PurchaseCreateLineDraft[]>(() => [createBlankPurchaseLine()]);
  const [error, setError] = React.useState<string | null>(null);

  const locationsQuery = useQuery({
    queryKey: ['admin', 'location-options'],
    queryFn: () => fetchLocationOptions(),
  });
  const vendorsQuery = useQuery({
    queryKey: ['admin', 'vendor-options'],
    queryFn: () => fetchVendorOptions(),
  });
  const activeVendors = React.useMemo(
    () => (vendorsQuery.data || []).filter((vendor) => vendor.active),
    [vendorsQuery.data],
  );

  React.useEffect(() => {
    // The dialog takes focus first; then the first field.
    const timer = window.setTimeout(() => locationRef.current?.focus(), 60);
    return () => window.clearTimeout(timer);
  }, []);

  const createMutation = useMutation({
    mutationFn: (po: NewPurchaseOrderInput) => requestKeys.run(JSON.stringify(po), (requestKey) => createPurchaseOrder({ requestKey, po })),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: purchaseOrderRecordKeys.all });
      if (onCreated) onCreated({ poId: created.poId, docNo: created.docNo });
      else navigate(`/transfers/purchase-orders/${encodeURIComponent(created.poId)}/lines`);
    },
    onError: (caught: unknown) => {
      setError(purchaseOrderErrorMessage(caught, 'The purchase order was not created. Try again.'));
    },
  });
  const creating = createMutation.isPending;

  function patchDraft(patch: Partial<CreateDraft>) {
    setDraft((prev) => ({ ...prev, ...patch }));
    setError(null);
  }

  function patchLine(id: string, patch: Partial<PurchaseCreateLineDraft>) {
    setLines((prev) => prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));
    setError(null);
  }

  function addLine() {
    setLines((prev) => [...prev, createBlankPurchaseLine()]);
  }

  function removeLine(id: string) {
    setLines((prev) => (prev.length > 1 ? prev.filter((entry) => entry.id !== id) : prev));
  }

  function handleClose() {
    if (!creating) onClose();
  }

  function handleCreate() {
    if (creating) return;
    if (!draft.locationId) {
      setError('Choose the site this order ships to.');
      return;
    }
    const normalized = normalizeLines(lines);
    if (!normalized.length) {
      setError('Add at least one line with a description.');
      return;
    }
    setError(null);
    createMutation.mutate({
      locationId: draft.locationId,
      vendorId: draft.vendorId || null,
      purchaseKind: draft.kind,
      expectedDeliveryAt: draft.expectedDay ? expectedDayToTimestamp(draft.expectedDay) : null,
      notes: draft.notes.trim() || null,
      eventId: null,
      lines: normalized,
    });
  }

  return (
    <DirectoryRecordDialog
      title="New purchase order"
      subtitle="The order gets its P-number when it is created. Add more lines on its record."
      size="lg"
      onClose={handleClose}
      closeDisabled={creating}
      error={error}
      errorTitle="The purchase order was not created"
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={creating}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating…' : 'Create purchase order'}
          </button>
        </>
      )}
    >
      <div className="directory-stack purchase-order-create">
        <div className="directory-fields">
          <label className="directory-field">
            <span className="directory-field__label">Site</span>
            <select
              ref={locationRef}
              value={draft.locationId}
              onChange={(event) => patchDraft({ locationId: event.target.value })}
              disabled={creating}
              required
            >
              <option value="">Choose a site</option>
              {(locationsQuery.data || []).map((location) => (
                <option key={location.location_id} value={location.location_id}>{location.name}</option>
              ))}
            </select>
          </label>
          <label className="directory-field">
            <span className="directory-field__label">Vendor</span>
            <select value={draft.vendorId} onChange={(event) => patchDraft({ vendorId: event.target.value })} disabled={creating}>
              <option value="">No vendor yet</option>
              {activeVendors.map((vendor) => (
                <option key={vendor.vendor_id} value={vendor.vendor_id}>{vendor.name}</option>
              ))}
            </select>
          </label>
          <label className="directory-field">
            <span className="directory-field__label">Type</span>
            <select
              value={draft.kind}
              onChange={(event) => patchDraft({ kind: event.target.value === 'labor' ? 'labor' : 'goods' })}
              disabled={creating}
            >
              <option value="goods">Goods</option>
              <option value="labor">Labor</option>
            </select>
          </label>
          <label className="directory-field">
            <span className="directory-field__label">Expected delivery</span>
            <input
              type="date"
              value={draft.expectedDay}
              onChange={(event) => patchDraft({ expectedDay: event.target.value })}
              disabled={creating}
            />
          </label>
          <label className="directory-field directory-field--wide">
            <span className="directory-field__label">Notes</span>
            <textarea rows={2} value={draft.notes} onChange={(event) => patchDraft({ notes: event.target.value })} disabled={creating} />
          </label>
        </div>
        <fieldset className="purchase-order-create__lines" disabled={creating}>
          <legend>Lines</legend>
          {lines.map((line, index) => (
            <div key={line.id} className="purchase-order-create__line">
              <label className="directory-field purchase-order-create__description">
                <span className="directory-field__label">Description</span>
                <input
                  placeholder={`Line ${index + 1} description`}
                  value={line.description}
                  onChange={(event) => patchLine(line.id, { description: event.target.value })}
                />
              </label>
              <label className="directory-field">
                <span className="directory-field__label">Qty</span>
                <input
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={line.qty}
                  onChange={(event) => patchLine(line.id, { qty: event.target.value })}
                />
              </label>
              <label className="directory-field">
                <span className="directory-field__label">Unit cost</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="0.00"
                  value={line.unitCost}
                  onChange={(event) => patchLine(line.id, { unitCost: event.target.value })}
                />
              </label>
              <label className="directory-field">
                <span className="directory-field__label">Use</span>
                <select
                  value={line.disposition}
                  aria-label={`Line ${index + 1} receiving use`}
                  onChange={(event) => patchLine(line.id, { disposition: event.target.value === 'event_sale' ? 'event_sale' : 'inventory' })}
                >
                  {LINE_USES.map((entry) => (
                    <option key={entry.value} value={entry.value}>{entry.label}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="btn btn-secondary purchase-order-create__remove"
                onClick={() => removeLine(line.id)}
                disabled={lines.length <= 1}
                aria-label={`Remove line ${index + 1}`}
              >
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-secondary sm purchase-order-create__add" onClick={addLine}>
            Add another line
          </button>
        </fieldset>
      </div>
    </DirectoryRecordDialog>
  );
}
